import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { Button, FormControl, MenuItem, Select, TextField } from '@mui/material';
import BottomSheetOrDialog from '../../../components/BottomSheetOrDialog/BottomSheetOrDialog';
import QRCode from 'react-qr-code';
import { toast } from 'react-toastify';
import IconToast from '../../../components/NewToast';

// Lucide Icons
import MapPin from 'lucide-react/dist/esm/icons/map-pin';
import Copy from 'lucide-react/dist/esm/icons/copy';
import MessageCircle from 'lucide-react/dist/esm/icons/message-circle';
import Check from 'lucide-react/dist/esm/icons/check';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import Send from 'lucide-react/dist/esm/icons/send';
import Share2 from 'lucide-react/dist/esm/icons/share-2';
import Smartphone from 'lucide-react/dist/esm/icons/smartphone';

import { THEME_PRESETS, INSURER_RECOMMENDED_THEME, buildTravelKitToken } from './tokenHelper';
import { getTravelKitUrl } from '../../../utils/travelKitUrl';
import { isSharable, share } from '../../../utils/native/share';
import { tracking } from '../../../utils/tracking';
import TravelKitCountryFlag from './TravelKitCountryFlag';
import TravelKitCountryPickerDialog from './TravelKitCountryPickerDialog';
import TravelKitPreviewDialog from './TravelKitPreviewDialog';
import { DEFAULT_DESTINATION_CODE, destinationName, findDestination, travelKitLangOfLocale } from './destinations';
import { buildClientMessage, buildClientShareTitle } from './clientMessage';
import './TravelKitLinkConstructor.scss';

export { DESTINATIONS } from './destinations';

/**
 * The 25 insurers, in source order — which is not the order they are shown in.
 * Add a new one anywhere; `INSURERS` below puts it in its place.
 */
const INSURER_OPTIONS = [
  { id: 'aia', name: 'AIA 友邦' },
  { id: 'aig', name: 'AIG 美亞保險' },
  { id: 'axa', name: 'AXA 安盛' },
  { id: 'bluecross', name: 'Blue Cross 藍十字' },
  { id: 'boclife', name: 'BOC Life 中銀人壽' },
  { id: 'bocg', name: 'BOCG 中銀集團保險' },
  { id: 'bowtie', name: 'Bowtie 保泰人壽' },
  { id: 'bupa', name: 'Bupa 保柏香港' },
  { id: 'chinalife', name: 'China Life 中國人壽海外' },
  { id: 'taiping', name: 'China Taiping 中國太平' },
  { id: 'chubb', name: 'Chubb 安達' },
  { id: 'cigna', name: 'Cigna 信諾環球' },
  { id: 'ctflife', name: 'CTF Life 周大福人壽' },
  { id: 'fwd', name: 'FWD 富衛' },
  { id: 'generali', name: 'Generali 忠意保險' },
  { id: 'hangseng', name: 'Hang Seng 恒生保險' },
  { id: 'hsbc', name: 'HSBC 滙豐保險' },
  { id: 'manulife', name: 'Manulife 宏利' },
  { id: 'msig', name: 'MSIG 三井住友保險' },
  { id: 'pingan', name: 'Ping An 中國平安' },
  { id: 'pru', name: 'Prudential 保誠' },
  { id: 'starr', name: 'Starr 史帶保險' },
  { id: 'sunlife', name: 'Sun Life 香港永明金融' },
  { id: 'yflife', name: 'YF Life 萬通保險' },
  { id: 'zurich', name: 'Zurich 蘇黎世' }
];

/**
 * A–Z on the Latin half of the name, with 「不指定」 pinned to the top.
 *
 * The list was hand-ordered by something like market size, which nobody could
 * verify and nothing documented — so finding an insurer in 26 options meant
 * reading all 26. Alphabetical also turns on MUI's own type-ahead: its
 * `MenuList` jumps to the first option whose text *starts with* what you type,
 * and every name here leads with its Latin form, so "chu" lands on Chubb.
 *
 * Sorted here rather than in the literal above so a name added to the list in
 * the wrong place still appears in the right one.
 */
export const INSURERS = [
  { id: '', name: '不指定保險公司（獨立經紀顧問）' },
  ...[...INSURER_OPTIONS].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
];

/**
 * The insurer an adviser last built a link with, remembered on this device.
 *
 * An adviser sells for one company and builds link after link for it, so the
 * field that never changes was the one they had to set every time — and the one
 * whose default (「不指定」) quietly published the wrong page when they forgot.
 *
 * Scoped to the adviser, in a single record rather than a key each, so a shared
 * office device does not hand the next person someone else's carrier. `''` is a
 * real remembered answer: an IFA who always picks 「不指定」 gets that back.
 */
const INSURER_MEMORY_KEY = 'pfp_travelkit_last_insurer_v1';

function readRememberedInsurer(adviserId) {
  if (!adviserId) return null;
  try {
    const saved = JSON.parse(window.localStorage.getItem(INSURER_MEMORY_KEY) || 'null');
    if (!saved || saved.adviserId !== adviserId) return null;
    // A carrier dropped from the list since must not preselect an id the
    // <Select> has no option for — MUI renders that as a blank field.
    return INSURERS.some(item => item.id === saved.insurer) ? saved.insurer : null;
  } catch {
    return null;
  }
}

function rememberInsurer(adviserId, insurer) {
  if (!adviserId) return;
  try {
    window.localStorage.setItem(INSURER_MEMORY_KEY, JSON.stringify({ adviserId, insurer }));
  } catch {
    // Private mode, or storage full. The default is a convenience; losing it
    // costs one dropdown, so nothing here is worth failing the dialog for.
  }
}

const TravelKitLinkConstructor = props => {
  const { intl, open, onClose, client, adviser } = props;

  // The adviser's own interface language, used for the form's labels. It is not
  // the link's language: that is `lang` below, which belongs to the client.
  const uiLang = travelKitLangOfLocale(intl.locale);

  const detectClientOrigin = () => {
    const phone = client?.phoneNumber || client?.phone || client?.mobile || '';
    if (phone.startsWith('+86') || phone.startsWith('86')) {
      return 'cn';
    }
    return 'hk';
  };

  // A mainland client reads Simplified. The origin toggle is labelled by
  // hotline (1868 / 12308) but it is really "which side is this client from",
  // so it carries the matching interface language with it — a link built for a
  // 內地居民 that opened in Traditional was a setting nobody remembered to
  // change, including when `detectClientOrigin` picked `cn` off a +86 number
  // and the language stayed on its hardcoded 'tc'.
  const originToLang = o => (o === 'cn' ? 'sc' : 'tc');

  const [country, setCountry] = useState(DEFAULT_DESTINATION_CODE);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [origin, setOrigin] = useState(detectClientOrigin);
  const [insurer, setInsurer] = useState('');
  const [theme, setTheme] = useState('bl');
  const [lang, setLang] = useState(() => originToLang(detectClientOrigin()));
  // Set once the adviser picks a language by hand; from then on the origin
  // toggle leaves it alone rather than silently discarding their choice.
  const [langTouched, setLangTouched] = useState(false);
  // Same idea for the insurer: once they have chosen, the remembered value
  // stops being applied, so a late-arriving `adviser` prop cannot overwrite it.
  const [insurerTouched, setInsurerTouched] = useState(false);

  const handleSelectOrigin = nextOrigin => {
    setOrigin(nextOrigin);
    if (!langTouched) {
      setLang(originToLang(nextOrigin));
    }
  };

  const handleSelectLang = nextLang => {
    setLang(nextLang);
    setLangTouched(true);
  };
  const [clientName, setClientName] = useState(() => (client?.name || client?.clientName || '').slice(0, 20));

  useEffect(() => {
    if (client?.name || client?.clientName) {
      setClientName((client.name || client.clientName).slice(0, 20));
    }
  }, [client]);

  // Whether the platform has a share sheet at all, read once — `isSharable()`
  // reaches Capacitor, and it cannot change while the dialog is open.
  const [canShare] = useState(isSharable);

  // Applied in an effect rather than as initial state: `adviser` comes from the
  // redux user and can arrive after this mounts, and without an id there is
  // nothing to look the memory up by. The theme follows, or a remembered
  // carrier would render in somebody else's blue.
  const adviserIdForMemory = adviser?._id || '';
  useEffect(() => {
    if (insurerTouched) return;
    const remembered = readRememberedInsurer(adviserIdForMemory);
    if (remembered === null) return;
    setInsurer(remembered);
    if (remembered && INSURER_RECOMMENDED_THEME[remembered]) {
      setTheme(INSURER_RECOMMENDED_THEME[remembered]);
    }
    // `insurerTouched` is deliberately not a dependency: this restores a
    // default, and re-running it after the adviser has chosen would undo them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adviserIdForMemory]);

  // The tick is a confirmation, not a mode. Cleared on its own, and on unmount
  // so a pending timer cannot set state on a dialog that has closed.
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  // A new link is a new thing to copy, so the old tick no longer applies.
  useEffect(() => setCopied(false), [country, insurer, theme, origin, lang, clientName]);

  const adviserId = adviser?._id || '';
  const trimmedName = clientName.trim();

  // Construct Scheme B compact SOS Paper URL: ?p=Country.Insurer.Ref.Theme.Origin.Lang
  const token = buildTravelKitToken({
    country,
    insurer,
    adviserId,
    theme,
    origin,
    lang
  });

  // Environment-based, not hardcoded: a beta build must link to beta-sos.
  let generatedUrl = `${getTravelKitUrl()}/?p=${token}`;
  if (trimmedName) {
    generatedUrl += `&n=${encodeURIComponent(trimmedName)}`;
  }

  const selectedDestination = findDestination(country);
  // The greeting is read by the client, so the country is named in the link's
  // language — not in whichever language the adviser happens to be working in.
  const countryDisplayName = destinationName(selectedDestination, lang);
  const activeTheme = THEME_PRESETS.find(preset => preset.id === theme) || THEME_PRESETS[0];

  const caringMessage = buildClientMessage({
    lang,
    clientName: trimmedName,
    countryName: countryDisplayName,
    url: generatedUrl
  });

  /**
   * What the adviser did with the link, and what kind of link it was.
   *
   * Opening this dialog is already tracked by each of its four entry points
   * (「TravelKit Link Constructor Accessed」); these are the steps after it, so the
   * funnel from opening to actually sending can be read. Tracked here rather
   * than at the entry points so every way in reports the same events.
   *
   * The settings ride along as properties. The client's name does not — only
   * whether one was entered — and neither does the URL, which carries it.
   *
   * Called after the action it reports, and never allowed to throw: the
   * adviser pressed a button to send a link, and a tracking library failing
   * must not be the reason nothing happened.
   */
  const trackAction = action => {
    try {
      tracking(`TravelKit Link: ${action}`, {
        destination: country,
        insurer: insurer || 'none',
        theme,
        origin,
        lang,
        hasClientName: Boolean(trimmedName)
      });
    } catch (error) {
      // Analytics only.
    }
  };

  // The tick and the toast wait on the clipboard's answer. Where the Clipboard
  // API is missing or refuses (an insecure context, some in-app WebViews) saying
  // 「已成功複製」 sent the adviser off to paste whatever they had copied before;
  // they are told it did not work instead. The event is the press either way.
  const handleCopyLink = () => {
    const onCopied = () => {
      // The icon becomes a tick as well as raising the toast: the button is
      // what was pressed, and a toast at the other end of the screen is easy
      // to miss.
      setCopied(true);
      toast.info(
        IconToast(
          'info',
          intl.formatMessage({
            id: 'travelkit-copied-toast',
            defaultMessage: '已成功複製旅行小錦囊連結！'
          })
        ),
        { className: 'new-toast' }
      );
    };
    const onFailed = () => {
      toast.info(
        IconToast(
          'error',
          intl.formatMessage({
            id: 'travelkit-copy-failed-toast',
            defaultMessage: '未能複製連結，請長按連結手動複製。'
          })
        ),
        { className: 'new-toast' }
      );
    };
    if (navigator.clipboard?.writeText) {
      Promise.resolve(navigator.clipboard.writeText(generatedUrl)).then(onCopied, onFailed);
    } else {
      onFailed();
    }
    trackAction('Copy Link');
  };

  const handleWhatsAppShare = () => {
    const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(caringMessage)}`;
    window.open(waUrl, '_blank');
    trackAction('WhatsApp Share');
  };

  // The platform's own share sheet — every channel the adviser actually uses
  // (Signal, WeChat, AirDrop, Messages) without one button each. `share()`
  // routes to the Capacitor plugin inside the app and to Web Share in a
  // browser; a cancelled sheet rejects, which is not an error worth reporting.
  // Text only: the message already carries the link on its own line, and a
  // separate `url` is appended again by iOS targets (WhatsApp, Messages) after
  // the last line, so the client got the link twice.
  // Tracked on the tap, not on the sheet resolving: iOS and Android do not
  // reliably report whether anything was actually sent.
  const handleNativeShare = () => {
    Promise.resolve(share({ title: buildClientShareTitle(lang), text: caringMessage })).catch(() => {});
    trackAction('Native Share');
  };

  const handleOpenPreview = () => {
    setPreviewOpen(true);
    trackAction('Preview Opened');
  };

  const header = intl.formatMessage({
    id: 'travelkit-link-constructor-title',
    defaultMessage: '製作旅行小錦囊'
  });

  const message = (id, defaultMessage) => intl.formatMessage({ id, defaultMessage });

  /**
   * A card with a line icon and a title.
   *
   * No explainer line under the title and no filled icon chip: six settings
   * each captioned with a sentence is more reading than the whole task is
   * worth, and a solid blue tile per card made four of them compete with the
   * one control that matters.
   */
  const section = (icon, title, children, extraClass) => (
    <section className={`travelkit-card ${extraClass || ''}`}>
      <div className="travelkit-card-head">
        <span className="travelkit-card-head-icon">{icon}</span>
        <h4>{title}</h4>
      </div>
      {children}
    </section>
  );

  // Short on the pill, full in the tooltip and to a screen reader. 「繁體中文」
  // and 「香港居民 1868」 do not fit five pills on one line, and the hotline
  // number is reference material, not a choice anyone makes by reading it.
  const LANG_OPTIONS = [
    { id: 'tc', short: '繁', full: '繁體中文' },
    { id: 'sc', short: '简', full: '简体中文' },
    { id: 'en', short: 'EN', full: 'English' }
  ];

  /**
   * Who reads the paper, on one line: identity pushed left, language right.
   *
   * No field labels — 「香港人」 and 「繁」 say what they are, so a 身分 / 語言
   * heading above each was a row spent restating the row. The hotline number
   * lives in the tooltip and the accessible name, where it is still reachable
   * without costing width. Both settings are derived from the client's phone
   * number anyway, so this row is usually read, not used.
   */
  const audienceRow = (
    <div className="travelkit-inline-row travelkit-span-2">
      <div className="travelkit-inline-group">
        <div
          className="travelkit-segmented"
          role="radiogroup"
          aria-label="Client Origin"
          style={{ '--seg-count': 2, '--seg-index': origin === 'cn' ? 1 : 0 }}
        >
          <button
            type="button"
            role="radio"
            aria-checked={origin === 'hk'}
            aria-label={message('travelkit-origin-hk-full', '香港居民 · 入境處 1868')}
            title={message('travelkit-origin-hk-full', '香港居民 · 入境處 1868')}
            className={`travelkit-segment ${origin === 'hk' ? 'is-active' : ''}`}
            onClick={() => handleSelectOrigin('hk')}
          >
            <TravelKitCountryFlag code="HK" flag="🇭🇰" size={15} />
            <span>{message('travelkit-origin-hk', '香港人')}</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={origin === 'cn'}
            aria-label={message('travelkit-origin-cn-full', '內地居民 · 外交部 12308')}
            title={message('travelkit-origin-cn-full', '內地居民 · 外交部 12308')}
            className={`travelkit-segment ${origin === 'cn' ? 'is-active' : ''}`}
            onClick={() => handleSelectOrigin('cn')}
          >
            <TravelKitCountryFlag code="CN" flag="🇨🇳" size={15} />
            <span>{message('travelkit-origin-cn', '內地人')}</span>
          </button>
        </div>
      </div>

      <div className="travelkit-inline-group">
        <div
          className="travelkit-segmented"
          role="radiogroup"
          aria-label="Default Language"
          style={{
            '--seg-count': LANG_OPTIONS.length,
            '--seg-index': Math.max(
              0,
              LANG_OPTIONS.findIndex(option => option.id === lang)
            )
          }}
        >
          {LANG_OPTIONS.map(option => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={lang === option.id}
              aria-label={option.full}
              title={option.full}
              className={`travelkit-segment ${lang === option.id ? 'is-active' : ''}`}
              onClick={() => handleSelectLang(option.id)}
            >
              <span>{option.short}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const content = (
    <div className="travelkit-constructor-body">
      <div className="travelkit-layout">
        <div className="travelkit-col">
          {section(
            <MapPin size={16} />,
            message('travelkit-section-paper', '錦囊設定'),
            <div className="travelkit-field-grid">
              {/* The destination opens a searchable picker rather than a dropdown —
                  the list is every country the SOS page can render, and it is the
                  one field an adviser gets wrong by scrolling past it. */}
              <button
                type="button"
                className="travelkit-picker-trigger travelkit-span-2"
                onClick={() => setCountryPickerOpen(true)}
                data-testid="travelkit-country-trigger"
              >
                <TravelKitCountryFlag code={selectedDestination?.code} flag={selectedDestination?.flag} size={30} />
                <span className="travelkit-picker-trigger-text">
                  <span className="travelkit-picker-trigger-label">{message('travelkit-country-label', '目的地')}</span>
                  <strong>{destinationName(selectedDestination, uiLang) || country}</strong>
                </span>
                <span className="travelkit-picker-trigger-change">{message('Change', '更改')}</span>
                <ChevronRight size={16} />
              </button>

              <div className="travelkit-field travelkit-span-2">
                <div className="travelkit-field-label">
                  <span>{message('travelkit-insurer-label', '顯示保險公司（可選）')}</span>
                </div>
                <FormControl fullWidth size="small" className="travelkit-input-ctrl">
                  <Select
                    value={insurer}
                    onChange={e => {
                      const val = e.target.value;
                      setInsurer(val);
                      setInsurerTouched(true);
                      // Written on the choice itself, not in an effect: only a
                      // deliberate pick should become the next default.
                      rememberInsurer(adviserIdForMemory, val);
                      if (val && INSURER_RECOMMENDED_THEME[val]) {
                        setTheme(INSURER_RECOMMENDED_THEME[val]);
                      }
                    }}
                    variant="outlined"
                  >
                    {INSURERS.map(item => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {/* Paired on purpose: a 20-character name and six swatches each
                  wasted most of a full-width row, and those two rows are what
                  pushed 客戶身分 / 語言 off a wide screen. */}
              <div className="travelkit-field">
                <div className="travelkit-field-label">
                  <span>{message('travelkit-client-name-label', '顯示客戶稱呼（可選）')}</span>
                  <span className="travelkit-char-counter">{clientName.length}/20</span>
                </div>
                <TextField
                  fullWidth
                  size="small"
                  value={clientName}
                  onChange={e => setClientName(e.target.value.slice(0, 20))}
                  placeholder={message('travelkit-client-name-placeholder', '例如：Peter（可留空）')}
                  // v9: `inputProps` no longer reaches the <input> — it lands on a
                  // div as an unknown DOM attribute and the maxLength is lost.
                  slotProps={{ htmlInput: { maxLength: 20 } }}
                  variant="outlined"
                  className="travelkit-input-ctrl"
                />
              </div>

              <div className="travelkit-field">
                <div className="travelkit-field-label">
                  <span>{message('travelkit-theme-label', '主題配色')}</span>
                </div>
                <div className="travelkit-swatches">
                  {THEME_PRESETS.map(preset => (
                    <button
                      key={preset.id}
                      type="button"
                      className={`travelkit-swatch ${theme === preset.id ? 'is-active' : ''}`}
                      style={{ backgroundColor: preset.color }}
                      onClick={() => setTheme(preset.id)}
                      title={preset.name}
                      aria-label={preset.name}
                      aria-pressed={theme === preset.id}
                    >
                      {theme === preset.id && <Check size={13} strokeWidth={3.2} color="#ffffff" />}
                    </button>
                  ))}
                </div>
              </div>

              {audienceRow}
            </div>
          )}
        </div>

        <div className="travelkit-col travelkit-col-send">
          {section(
            <Send size={16} />,
            message('travelkit-section-share', '發送給客戶'),
            <div className="travelkit-share">
              <div className="travelkit-share-qr">
                <div className="travelkit-qr-frame">
                  <QRCode
                    value={generatedUrl}
                    size={104}
                    level="Q"
                    style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                    viewBox="0 0 256 256"
                  />
                  <div className="travelkit-qr-logo">
                    <img src="/img/QRCode.svg" alt="PortfoPlus" />
                  </div>
                </div>
                <span className="travelkit-qr-hint">{message('travelkit-qr-hint', '給客戶掃描')}</span>
              </div>

              {/* Under the QR, and with no heading: the URL is plainly a URL,
                  and it is the same link the code above encodes — a 「專屬連結：」
                  label spent a row saying what the monospace text already
                  says. Copy is an icon on the field it copies. */}
              <div className="travelkit-preview-url" title={generatedUrl}>
                <span>{generatedUrl}</span>
                <button
                  type="button"
                  className={`travelkit-copy-icon ${copied ? 'is-copied' : ''}`}
                  onClick={handleCopyLink}
                  aria-label={message('travelkit-copy-link', '複製連結')}
                  title={message('travelkit-copy-link', '複製連結')}
                  data-testid="travelkit-copy-link"
                >
                  {copied ? <Check size={14} strokeWidth={3} /> : <Copy size={14} />}
                </button>
              </div>

              {/* Before the send buttons: seeing the page is what makes sending
                  it feel safe. Outline, so it never outranks WhatsApp. */}
              <button
                type="button"
                className="travelkit-preview-trigger"
                onClick={handleOpenPreview}
                data-testid="travelkit-preview-open"
              >
                <Smartphone size={15} />
                <span>{message('travelkit-preview-open', '預覽客戶看到的頁面')}</span>
              </button>

              <div className="travelkit-share-actions">
                <Button className="travelkit-btn travelkit-btn-whatsapp" variant="contained" onClick={handleWhatsAppShare}>
                  <MessageCircle size={15} />
                  <span>{message('travelkit-whatsapp-share', 'WhatsApp')}</span>
                </Button>

                {/* Only where the platform actually has a share sheet — on
                    desktop web without one, `isSharable()` is false and the
                    copy icon above is the answer instead of a dead button. */}
                {canShare && (
                  <Button
                    className="travelkit-btn travelkit-btn-share"
                    variant="contained"
                    onClick={handleNativeShare}
                    data-testid="travelkit-native-share"
                  >
                    <Share2 size={15} />
                    <span>{message('travelkit-share-native', '分享')}</span>
                  </Button>
                )}
              </div>
            </div>,
            'travelkit-card-share'
          )}
        </div>
      </div>

      <TravelKitCountryPickerDialog
        open={countryPickerOpen}
        value={country}
        onSelect={setCountry}
        onClose={() => setCountryPickerOpen(false)}
      />

      <TravelKitPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        url={generatedUrl}
        labels={{
          title: message('travelkit-preview-title', '客戶看到的頁面'),
          hint: message('travelkit-preview-hint', '僅供預覽，可上下捲動，按鈕不能點擊。'),
          close: message('Close', '關閉')
        }}
      />
    </div>
  );

  return (
    <BottomSheetOrDialog
      className="travelkit-link-constructor-dialog"
      open={open}
      onClose={onClose}
      header={header}
      content={content}
      actions={[]}
      // The tinted ground belongs to `.travelkit-constructor-body`, which owns its own
      // padding — the container's default inset would leave a white gutter down
      // both sides of the cards.
      DialogContentProps={{ style: { padding: 0 } }}
      DialogProps={{
        maxWidth: 'md',
        fullWidth: true,
        PaperProps: {
          style: {
            maxWidth: 880,
            borderRadius: 18
          }
        }
      }}
      BottomSheetProps={{
        expandOnContentDrag: false,
        disableAutoDismiss: true,
        BoxProps: { sx: { padding: 0 } }
      }}
    />
  );
};

export default injectIntl(TravelKitLinkConstructor);
